import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { plansStripe } from '../services/api';
import { CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ loading: true, ok: false, isGuest: false });
  const navigate = useNavigate();
  useEffect(() => {
    if (!state.ok || state.isGuest) return;
    const t = setTimeout(() => navigate('/business/manage?create=1'), 2500);
    return () => clearTimeout(t);
  }, [state.ok, state.isGuest, navigate]);

  useEffect(() => {
    if (!sessionId) { setState({ loading: false, ok: false }); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const s = await plansStripe.status(sessionId);
        if (s.paymentStatus === 'paid') {
          setState({ loading: false, ok: true, isGuest: !!s.isGuest, lookupKey: s.lookupKey });
          return;
        }
      } catch {}
      attempts += 1;
      if (attempts > 15) { setState({ loading: false, ok: false }); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 text-neutral-900 p-4" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center border border-neutral-200">
        {state.loading ? (
          <>
            <Loader2 className="mx-auto animate-spin text-green-600" size={40}/>
            <div className="mt-4 text-lg font-semibold">Confirming your subscription…</div>
            <p className="text-sm text-neutral-500 mt-1">Stripe usually takes a couple of seconds.</p>
          </>
        ) : state.ok ? (
          <>
            <CheckCircle2 className="mx-auto text-green-600" size={48}/>
            <h1 className="mt-3 text-2xl font-black">Subscription active!</h1>
            <p className="text-sm text-neutral-600 mt-2">
              You're subscribed to the PBB Tier 1 plan. {state.isGuest && 'Finish creating your account so we can attach the plan to your first listing.'}
            </p>
            <Link to={state.isGuest ? '/signup' : '/business/manage?create=1'} data-testid="payment-success-cta"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-500 hover:bg-green-400 text-black font-bold shadow-lg">
              {state.isGuest ? 'Create my account' : 'Create my listing'} <ArrowRight size={14}/>
            </Link>
          </>
        ) : (
          <>
            <XCircle className="mx-auto text-red-500" size={48}/>
            <h1 className="mt-3 text-2xl font-black">We couldn't confirm your payment.</h1>
            <p className="text-sm text-neutral-600 mt-2">
              If Stripe already charged you, refresh this page in a few seconds. Otherwise, try again.
            </p>
            <Link to="/plans" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-neutral-900 text-white">
              Back to plans
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
